import { type ReactNode, useMemo } from 'react';
import { displayCell, isFigure } from '@shared/cell-format';
import { Avatar } from '@/components/apps/Avatar';
import { StatusPicker } from '@/components/apps/StatusPicker';
import { ToneDot } from '@/components/views/atoms/ToneDot';
import { LabelChip } from '@/components/apps/AppLabelsField';
import type { AppLabel } from '@/lib/app-collaboration';
import { cn } from '@/lib/utils';
import type { AppCollection, AppMember, AppRecord } from '@/lib/app-records';
import { cellText, columnFormats, isDone, listColumns, recordTitle, seenLabel } from './app-records-model';
import { CellValue, ObjectBadge } from './CellValue';

interface AppRecordsTableProps {
  collection: AppCollection;
  records: AppRecord[];
  members: AppMember[];
  selectedId: string | null;
  labels?: AppLabel[];
  labelsByRecord?: Record<string, string[]>;
  onSelect: (record: AppRecord) => void;
  onStatus: (record: AppRecord, status: string) => void;
  /** F6: the columns the app's description shows, in order, after the title; unknown keys are skipped. */
  visibleColumns?: string[];
  /** Shown in the table's body when there is no row: the headers still say what the list will hold. */
  empty?: ReactNode;
  /** N2: the first next action of each object, when Domino planned it. */
  nextActions?: Record<string, { title: string; count: number }> | null;
  /** S1: rows can be checked when a tool is proposed on them. */
  selection?: { ids: Set<string>; onToggle: (recordId: string, on: boolean) => void; onToggleAll: (on: boolean) => void } | null;
}

export const AppRecordsTable = ({ collection, records, members, selectedId, labels = [], labelsByRecord = {}, onSelect, onStatus, visibleColumns, empty, nextActions, selection }: AppRecordsTableProps) => {
  const labelsOf = (record: AppRecord) => labels.filter((label) => (labelsByRecord[record.id] ?? []).includes(label.id));
  const nameOf = (userId: string | null) => members.find((member) => member.userId === userId)?.name ?? null;
  const formats = useMemo(() => columnFormats(collection, records), [collection, records]);
  const columns = [collection.columns[0], ...listColumns(collection, visibleColumns, formats)]
    .filter((column): column is NonNullable<typeof column> => column !== undefined);
  // U2: a company or a person sits with its badge, its domain under its name when the list does not show it.
  const badged = collection.objectKind === 'company' || collection.objectKind === 'person';
  const domainKey = collection.columns.find((column) => column.role === 'domain' && !columns.includes(column))?.key ?? null;
  const domainOf = (record: AppRecord) => domainKey ? cellText(record.computed.find((cell) => cell.key === domainKey)) : '';
  const changedBy = (record: AppRecord) => record.statusChangedBy ? nameOf(record.statusChangedBy) : null;

  return (
    <>
      {records.length === 0 && <div className="rounded-xl border border-domino-cardBorder px-4 py-8 text-center sm:hidden">{empty}</div>}
      <ul className="space-y-2 sm:hidden" aria-label={collection.title}>
        {records.map((record) => (
          <li key={record.id}>
            <div role="button" tabIndex={0} onClick={() => onSelect(record)} onKeyDown={(event) => { if (event.key === 'Enter') onSelect(record); }}
              className={cn('rounded-xl border border-domino-cardBorder bg-domino-warm p-3', isDone(collection, record) && 'opacity-60')}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2.5">
                  {badged && <ObjectBadge name={recordTitle(record, collection)} />}
                  <div className="min-w-0">
                  <p className="text-sm font-semibold text-domino-ink">{recordTitle(record, collection)}</p>
                  {domainOf(record) && <p className="truncate text-xs text-domino-inkSoft">{domainOf(record)}</p>}
                  {labelsOf(record).length > 0 && <div className="mt-1 flex flex-wrap gap-1">{labelsOf(record).map((label) => <LabelChip key={label.id} label={label} />)}</div>}
                  </div>
                </div>
                {record.assigneeUserId && nameOf(record.assigneeUserId) && <Avatar name={nameOf(record.assigneeUserId)!} size="xs" />}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <StatusPicker statuses={collection.statuses} value={record.status} onChange={(status) => onStatus(record, status)} />
                {changedBy(record) && <Avatar name={changedBy(record)!} size="xs" />}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden overflow-x-auto rounded-xl border border-domino-cardBorder sm:block">
        <table className="min-w-full text-sm">
          <thead className="bg-domino-muted text-left text-xs font-medium text-domino-inkSoft">
            <tr>
              {selection && (
                <th className="w-8 py-2 pl-3">
                  <input type="checkbox" aria-label="Tout sélectionner" checked={records.length > 0 && records.every((record) => selection.ids.has(record.id))}
                    onChange={(event) => selection.onToggleAll(event.target.checked)} />
                </th>
              )}
              <th className="px-3 py-2">Statut</th>
              {columns.map((column) => <th key={column.key} className={cn('whitespace-nowrap px-3 py-2', isFigure(formats[column.key] ?? 'text') && 'text-right')}>{column.label}</th>)}
              {nextActions && <th className="px-3 py-2">Prochaine action</th>}
              <th className="px-3 py-2">Assignée à</th>
              <th className="px-3 py-2">Vue</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && (
              <tr className="border-t border-domino-cardBorder">
                <td colSpan={columns.length + 3 + (nextActions ? 1 : 0) + (selection ? 1 : 0)} className="px-3 py-10 text-center">{empty}</td>
              </tr>
            )}
            {records.map((record) => (
              <tr key={record.id} onClick={() => onSelect(record)}
                className={cn('cursor-pointer border-t border-domino-cardBorder align-middle hover:bg-domino-warm',
                  record.id === selectedId && 'bg-domino-warm', isDone(collection, record) && 'opacity-60')}>
                {selection && (
                  <td className="w-8 py-2 pl-3" onClick={(event) => event.stopPropagation()}>
                    <input type="checkbox" aria-label={`Sélectionner ${recordTitle(record, collection)}`} checked={selection.ids.has(record.id)}
                      onChange={(event) => selection.onToggle(record.id, event.target.checked)} />
                  </td>
                )}
                <td className="whitespace-nowrap px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <StatusPicker statuses={collection.statuses} value={record.status} onChange={(status) => onStatus(record, status)} />
                    {changedBy(record) && <Avatar name={changedBy(record)!} size="xs" />}
                  </div>
                </td>
                {columns.map((column, index) => {
                  const cell = record.computed.find((item) => item.key === column.key);
                  const format = formats[column.key] ?? 'text';
                  if (index === 0) {
                    const title = cellText(cell) || recordTitle(record, collection);
                    return (
                      <td key={column.key} className="px-3 py-2 text-domino-ink">
                        <span className="flex items-center gap-2.5">
                          {badged && <ObjectBadge name={title} />}
                          <span className="min-w-0">
                            <span className={cn('block max-w-[260px] truncate font-medium', isDone(collection, record) && 'line-through decoration-domino-inkSoft/50')} title={title}>
                              {cell?.tone && <ToneDot tone={cell.tone} className="mr-1.5 inline-block align-middle" />}{title}
                            </span>
                            {domainOf(record) && <span className="block max-w-[260px] truncate text-xs text-domino-inkSoft">{domainOf(record)}</span>}
                          </span>
                        </span>
                        {labelsOf(record).length > 0 && (
                          <span className="mt-1 flex flex-wrap gap-1">{labelsOf(record).map((label) => <LabelChip key={label.id} label={label} />)}</span>
                        )}
                      </td>
                    );
                  }
                  return (
                    <td key={column.key} className={cn('max-w-[260px] px-3 py-2 text-domino-ink', isFigure(format) && 'text-right')}>
                      <CellValue display={displayCell(format, cell)} tone={cell?.tone} list />
                    </td>
                  );
                })}
                {nextActions && (
                  <td className="px-3 py-2 text-domino-ink">
                    {nextActions[record.id] ? (
                      <span className="flex items-start gap-1.5">
                        <span className="block min-w-[8rem] max-w-[260px] truncate" title={nextActions[record.id]!.title}>{nextActions[record.id]!.title}</span>
                        {nextActions[record.id]!.count > 1 && <span className="shrink-0 rounded-full bg-domino-muted px-1.5 text-xs text-domino-inkSoft">+{nextActions[record.id]!.count - 1}</span>}
                      </span>
                    ) : <span className="text-domino-inkSoft">—</span>}
                  </td>
                )}
                <td className="px-3 py-2">{record.assigneeUserId && nameOf(record.assigneeUserId) ? <Avatar name={nameOf(record.assigneeUserId)!} /> : <span className="text-domino-inkSoft">—</span>}</td>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-domino-inkSoft">{seenLabel(record.lastSeenAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
